import React, { useCallback, useEffect } from 'react'
import { Button, Modal } from 'antd'
import {
  MAX_UPLOAD_FILE_SIZE_BYTES,
  type SurecoCsvDataType,
} from '../constants'
import { useCsvDataTypeDetection } from '../hooks/useCsvDataTypeDetection'
import { CsvDetectionPhaseContent } from './CsvDetectionPhaseContent'
import CsvFileDropzone from './CsvFileDropzone'
import styles from '../PlanManagementV3.module.scss'

export interface UploadAnalyzeCsvParams {
  dataType: SurecoCsvDataType
  file: File
}

interface Props {
  open: boolean
  isAnalyzing: boolean
  onClose: () => void
  onAnalyze: (params: UploadAnalyzeCsvParams) => void
}

export default function UploadAnalyzeCsvModal({
  open,
  isAnalyzing,
  onClose,
  onAnalyze,
}: Props) {
  const {
    phase,
    file,
    setFile,
    detectedDataType,
    columnErrors,
    detect,
    reset,
  } = useCsvDataTypeDetection((dataType, detectedFile) => {
    // reset() first so `phase` is already idle before the close-triggered
    // reopen effect would otherwise catch up, avoiding a stale-UI flash.
    reset()
    onAnalyze({ dataType, file: detectedFile })
    onClose()
  })

  // Reset fields each time the modal is (re)opened.
  useEffect(() => {
    if (open) reset()
  }, [open, reset])

  const handleAnalyzeClick = useCallback(() => {
    void detect()
  }, [detect])

  // reset() before close cancels any in-flight detect() immediately.
  const handleCancel = useCallback(() => {
    reset()
    onClose()
  }, [reset, onClose])

  const isFilePickerPhase = phase === 'idle'

  return (
    <Modal
      open={open}
      title="Upload File"
      maskClosable
      onCancel={handleCancel}
      destroyOnClose
      centered
      width={640}
      footer={
        phase === 'noMatch'
          ? [
              <Button key="tryAgain" type="primary" onClick={reset}>
                Try again
              </Button>,
            ]
          : phase === 'columnErrors'
            ? [
                <Button key="cancel" onClick={handleCancel}>
                  Cancel
                </Button>,
                <Button key="continue" type="primary" onClick={reset}>
                  Continue
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={handleCancel}>
                  Cancel
                </Button>,
                <Button
                  key="confirm"
                  type="primary"
                  loading={phase === 'detecting' || isAnalyzing}
                  disabled={!file}
                  onClick={handleAnalyzeClick}
                >
                  Analyze
                </Button>,
              ]
      }
    >
      <div className={styles.modalContent}>
        {phase !== 'idle' && (
          <CsvDetectionPhaseContent
            phase={phase}
            detectedDataType={detectedDataType}
            columnErrors={columnErrors}
          />
        )}

        {isFilePickerPhase && (
          <div className={styles.modalField}>
            <CsvFileDropzone
              file={file}
              onFileSelect={setFile}
              onRemoveFile={() => setFile(null)}
              maxFileSizeBytes={MAX_UPLOAD_FILE_SIZE_BYTES}
            />
          </div>
        )}
      </div>
    </Modal>
  )
}
